<script setup lang="ts">
import { useSettingsStore, Language, DateLocale } from '@/store/settings'
import { useI18n } from 'vue-i18n'
import { useBranchString } from '@/composables/version.js'
import FLAGS from '@/assets/flags'

type LanguageOption = {
  id: Language
  title: string
  avatar: string
  dateLocale: DateLocale
}

const props = defineProps({
  hideIcon: { type: Boolean, default: false },
  hideDetails: { type: Boolean, default: false },
})

const settingsStore = useSettingsStore()
const { locale } = useI18n({ useScope: 'global' })

const availableLanguages: LanguageOption[] = [
  { id: 'de', title: 'Deutsch', avatar: FLAGS['de'], dateLocale: 'de-DE' },
  { id: 'en', title: 'English', avatar: FLAGS['en'], dateLocale: 'en-US' },
]

if (['dev', 'staging', 'preview', 'local'].includes(useBranchString()))
  availableLanguages.push({ id: 'debug', title: 'DEBUG', avatar: FLAGS['debug'], dateLocale: 'scientific' })

function updateLanguage(language: Language) {
  const selectedLanguage = availableLanguages.find(lang => lang.id === language)
  settingsStore.setLanguage(language)
  settingsStore.setDateLocale(selectedLanguage?.dateLocale ?? 'de-DE')
  locale.value = language
}
</script>

<template>
  <v-select
    :prepend-icon="props.hideIcon ? undefined : 'mdi-translate'"
    variant="outlined"
    density="comfortable"
    :items="availableLanguages"
    :model-value="settingsStore.language"
    item-text="title"
    item-value="id"
    :item-props="true"
    :hide-details="props.hideDetails"
    @update:model-value="updateLanguage"
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item
        v-bind="itemProps"
        :title="item?.raw?.title"
      >
        <template #prepend>
          <v-avatar
            :image="item?.raw?.avatar"
            size="x-small"
          />
        </template>
      </v-list-item>
    </template>
    <template #selection="{ item }">
      <v-list-item
        class="px-2"
        :title="item?.raw?.title"
        slim
        density="compact"
        :lines="false"
      >
        <template #prepend>
          <div class="mr-4">
            <v-avatar
              :image="item?.raw?.avatar"
              size="x-small"
            />
          </div>
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>
